import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function ErrorHandlingPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Error Handling in Flows</h1>
          <p className="text-lg text-gray-600 mt-2">
            Building robust flows that gracefully handle failures
          </p>
        </div>

        {/* Warning Alert */}
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-orange-800">Missing Error Handling</h3>
              <p className="mt-2 text-sm text-orange-700">
                Flows without error handling can fail silently or leave data in an inconsistent state.
                When DML operations or queries fail, your flow stops - but without error handling,
                you won't know why or be able to recover.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Is Error Handling?</h2>
          <p className="text-gray-700 mb-4">
            Error handling is the practice of adding fault paths to your flows that execute when
            operations fail. Every element that performs a database operation (Get Records, Update Records,
            Create Records, Delete Records) can fail - and you need to handle those failures.
          </p>

          <div className="mt-4 p-4 bg-gray-50 rounded">
            <p className="text-sm font-semibold text-gray-900 mb-2">Common Causes of Flow Failures:</p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
              <li>Required fields missing on record creation</li>
              <li>Validation rules blocking updates</li>
              <li>Sharing/permission issues preventing access</li>
              <li>Governor limits exceeded (SOQL, DML, CPU time)</li>
              <li>Null reference errors when expected data doesn't exist</li>
              <li>Formula evaluation errors</li>
            </ul>
          </div>
        </div>

        {/* Why is it important? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Is Error Handling Important?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-900">Prevents Silent Failures</h3>
              <p className="text-gray-700 mt-1">
                Without error handling, your flow simply stops when it hits an error. Users may not
                realize the automation failed, leading to incomplete processes and data inconsistencies.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Enables Debugging</h3>
              <p className="text-gray-700 mt-1">
                Error handling allows you to log failures, send notifications, or write error details
                to a custom object - making it much easier to troubleshoot issues.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-gray-900">Provides Graceful Degradation</h3>
              <p className="text-gray-700 mt-1">
                With proper error handling, you can implement fallback logic, retry mechanisms,
                or alternative paths when the primary operation fails.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900">Improves User Experience</h3>
              <p className="text-gray-700 mt-1">
                For screen flows, you can show users meaningful error messages instead of cryptic
                system errors, helping them understand what went wrong and how to fix it.
              </p>
            </div>
          </div>
        </div>

        {/* How to identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Missing Error Handling</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check the Performance Scorecard on the flow detail page</li>
                <li>Look for "Missing Error Handling" in the Issues Detected section</li>
                <li>Review the deduction amount - indicates number of DML operations without error handling</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In Salesforce Flow Builder:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Look for Create/Update/Delete/Get Records elements</li>
                <li>Check if these elements have a Fault Connector (lightning bolt icon)</li>
                <li>The Fault Connector should connect to error handling logic</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Elements That Need Error Handling:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li><strong>Create Records</strong> - Can fail due to required fields, validation rules</li>
                <li><strong>Update Records</strong> - Can fail due to permissions, validation rules</li>
                <li><strong>Delete Records</strong> - Can fail due to child record dependencies</li>
                <li><strong>Get Records</strong> - Less critical, but should handle no results found</li>
              </ul>
            </div>
          </div>
        </div>

        {/* How to implement */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Implement Error Handling</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Solution</h3>
                <p className="mt-2 text-sm text-green-700">
                  Add a Fault Connector to critical elements and connect it to error handling logic
                  that logs the error, notifies stakeholders, or implements fallback behavior.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Step-by-Step Implementation:</h3>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add Fault Connector</h4>
                  <p className="text-gray-700 mt-1">
                    Click on the element you want to add error handling to, then drag from the
                    lightning bolt (⚡) icon at the bottom of the element.
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Create Error Handling Logic</h4>
                  <p className="text-gray-700 mt-1">
                    Choose your error handling strategy based on your needs:
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm font-semibold text-gray-900">Option A: Email Notification</p>
                      <p className="text-sm text-gray-700">Send an alert to the admin team</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm font-semibold text-gray-900">Option B: Error Logging</p>
                      <p className="text-sm text-gray-700">Create a record in a custom Error Log object</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm font-semibold text-gray-900">Option C: User Notification</p>
                      <p className="text-sm text-gray-700">Show an error screen with helpful message</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm font-semibold text-gray-900">Option D: Fallback Logic</p>
                      <p className="text-sm text-gray-700">Try alternative approach or default values</p>
                    </div>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Capture Error Details</h4>
                  <p className="text-gray-700 mt-1">
                    Use system variables to capture error information:
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm space-y-1">
                    <div><strong>$Flow.FaultMessage</strong> - Error message text</div>
                    <div><strong>$Flow.CurrentRecord</strong> - Record that caused the error</div>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Test Error Scenarios</h4>
                  <p className="text-gray-700 mt-1">
                    Deliberately trigger errors to verify your error handling works:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 mt-2 space-y-1">
                    <li>Try creating records with missing required fields</li>
                    <li>Attempt updates that violate validation rules</li>
                    <li>Test with users who lack proper permissions</li>
                  </ul>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (No Error Handling)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">// Create opportunity</div>
                <div className="pl-4">Create Records: New Opportunity</div>
                <div className="pl-4 text-red-600">⚡ No fault connector</div>
                <div className="mt-2 text-gray-600">// Update account</div>
                <div className="pl-4">Update Records: Account</div>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Result: If creation fails, flow stops silently. User and admin unaware of failure.
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (With Error Handling)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">// Create opportunity</div>
                <div className="pl-4">Create Records: New Opportunity</div>
                <div className="pl-4 text-green-600">⚡ Fault → Error Handler</div>
                <div className="mt-2 pl-8 text-green-600">Send Email Alert</div>
                <div className="pl-8 text-green-600">Create Error Log Record</div>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: Admin receives alert with error details. Error logged for troubleshooting.
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Handling Best Practices</h2>

          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Always Handle DML Operations</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Every Create, Update, and Delete Records element should have error handling
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Provide Meaningful Error Messages</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Include context: which record, what operation, why it failed
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Log Errors for Later Analysis</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Create a custom Error_Log__c object to track all flow failures
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Test Your Error Paths</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Error handling is only useful if it actually works - test it!
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Don't Swallow Errors Silently</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Always notify someone or log the error - don't just connect to an empty screen
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Benefits</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Reliability: Significantly improved</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Before: Silent failures, no visibility into issues</li>
              <li>After: All failures logged and reported</li>
            </ul>
            <p className="mt-4">
              Proper error handling transforms unreliable automations into robust, production-ready
              processes that provide visibility and enable rapid troubleshooting.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to building high-quality flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/dml-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                DML Operations in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Another common cause of flow failures</p>
            </li>
            <li>
              <Link to={`${basePath}/help/bulkification`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Bulkification →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Building flows that handle high data volumes</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
